import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { Rating, ratingToLetter } from 'domain/types';
import { colors, styled } from 'core/styles';
import { ratingColors } from 'domain/utils';
import { DateLabel } from 'components';
import { Either } from 'utils/either';

import { RatingHistoryTooltip } from './RatingHistoryTooltip';

export type RatingHistory = {
  rating: Rating;
  timestamp: number;
};

export function RatingHistoryChart({
  history,
  rating,
}: {
  history: RatingHistory[];
  rating: Either<Rating>;
}) {
  const colorLine = rating.fold(
    r => ratingColors[r],
    () => ratingColors['N/A'],
  );
  // TODO: replace with timestamp
  const { timestamp } = history[0];
  const date = useMemo(() => new Date(timestamp), [timestamp]);

  const renderYTick = React.useCallback((tickProps: any) => {
    const {
      x,
      y,
      payload: { value },
    } = tickProps;

    return (
      <text x={x} y={y + 10} dx={6} dy={-6} fill="#fff" fontSize={10}>
        {ratingToLetter(value)}
      </text>
    );
  }, []);

  return (
    <Root>
      <RatingHistoryTitle>
        Rating last <DateLabel date={date} />
      </RatingHistoryTitle>
      <RatingHistoryContainer>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={history}
            margin={{
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid vertical={false} stroke={colors.signalBlack} strokeWidth={0.5} />
            <XAxis hide dataKey="timestamp" />
            <YAxis
              mirror
              tickLine={false}
              axisLine={false}
              stroke="#fff"
              strokeWidth={2}
              // domain={[0, 4]}
              ticks={[0, 1, 2, 3, 4]}
              tick={renderYTick}
              padding={{ bottom: 20, top: 20 }}
            />
            <Tooltip content={<RatingHistoryTooltip />} />
            <Line
              dot={false}
              type="monotone"
              dataKey="rating"
              stroke={colorLine}
              activeDot={{ r: 4 }}
              strokeWidth={1.5}
            />
          </LineChart>
        </ResponsiveContainer>
      </RatingHistoryContainer>
    </Root>
  );
}

const Root = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  width: '100%',
}));

const RatingHistoryContainer = styled('div')(() => ({
  height: '144px',
  borderRadius: 8,
  border: `1px solid ${colors.signalBlack}`,
}));

const RatingHistoryTitle = styled('div')(() => ({
  fontSize: 10,
  color: colors.whiteOpacity[40],
}));
